/**
 * Servicio API para el motor de conciliación de bookings
 *
 * Endpoints:
 *   POST api/bookings/reconciliation/run       — matching automático en cascada
 *   GET  api/bookings/reconciliation/{id}/candidates — candidatos para vinculación manual
 *   POST api/bookings/reconciliation/link      — vincular par naviera + repo doc
 *   POST api/bookings/reconciliation/unlink    — desvincular booking
 */
import { appConfig } from "@/config/app-config";
import type {
  RunMatchingRequest,
  RunMatchingResultDto,
  MatchCandidateDto,
  NavieraBookingSearchResult,
  LinkBookingEquipmentRequest,
  ConfirmSharedShipmentRequest,
  ConfirmSharedShipmentResponse,
  UnlinkBookingRequest,
  BookingEquipmentReconciliationLinkDto,
  GetRepoDocReconciliationHealthRequest,
  RepoDocReconciliationHealthDto,
  GetNavieraReconciliationHealthRequest,
  NavieraReconciliationHealthDto,
  GetContractAssignmentHealthRequest,
  ContractAssignmentHealthDto,
  GetContractAssignmentHealthCandidatesRequest,
  ContractAssignmentHealthCandidatesDto,
  ConfirmContractAssignmentRequest,
  ConfirmContractAssignmentResponse,
  ConfirmBookingLevelReconciliationRequest,
  UnlinkBookingLevelReconciliationRequest,
  ApiResponse,
  NavieraHealthCategory,
} from "@/types/reconciliation";

const CONFIRM_SHARED_SHIPMENT_SUCCESS_MESSAGE = "Embarque compartido confirmado correctamente";

// ═══════════════════════════════════════════════════════════════════════════
// Auth helper (igual que en imports-api.ts)
// ═══════════════════════════════════════════════════════════════════════════

function getAuthToken(): string | null {
  try {
    const oidcKey = `oidc.user:${appConfig.oidc.authority}:${appConfig.oidc.clientId}`;
    const oidcData = sessionStorage.getItem(oidcKey);
    if (oidcData) {
      const parsed = JSON.parse(oidcData);
      return parsed.access_token || null;
    }
  } catch {
    // silencioso — el caller recibirá 401 del servidor
  }
  return null;
}


function appendQuery(url: string, params: Record<string, unknown>): string {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    query.set(key, String(value));
  });
  const qs = query.toString();
  return qs ? `${url}?${qs}` : url;
}

function getAuthHeaders(): HeadersInit {
  const token = getAuthToken();
  const headers: HeadersInit = { "Content-Type": "application/json" };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  return headers;
}

// ═══════════════════════════════════════════════════════════════════════════
// Helper interno: lanza error con el mensaje del backend si lo hay
// ═══════════════════════════════════════════════════════════════════════════

async function parseErrorMessage(response: Response): Promise<string> {
  try {
    const body = await response.json();
    // El backend puede devolver { error: "..." } o { title: "..." } o un string
    return body?.error ?? body?.title ?? body?.message ?? `Error ${response.status}`;
  } catch {
    return `Error ${response.status}`;
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// POST api/bookings/reconciliation/run
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Ejecuta el matching automático en cascada (L1 → L2 → L3) para una entidad.
 * @throws Error con mensaje del backend si la petición falla
 */
export async function runMatching(
  request: RunMatchingRequest
): Promise<RunMatchingResultDto> {
  const url = appConfig.getApiUrl("bookings/reconciliation/run");
  const response = await fetch(url, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const msg = await parseErrorMessage(response);
    throw new Error(msg);
  }

  const json: ApiResponse<RunMatchingResultDto> = await response.json();
  const result = json.data;
  if ((json as any).message && result) {
    (result as any)._message = (json as any).message;
  }
  return result;
}

// ═══════════════════════════════════════════════════════════════════════════
// GET api/bookings/reconciliation/{bookingId}/candidates
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Devuelve candidatos de matching para un booking específico, agrupados por nivel.
 * @throws Error con mensaje del backend si la petición falla
 */
export async function getMatchCandidates(
  bookingId: number
): Promise<MatchCandidateDto[]> {
  const url = appConfig.getApiUrl(`bookings/reconciliation/${bookingId}/candidates`);
  const response = await fetch(url, {
    method: "GET",
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const msg = await parseErrorMessage(response);
    throw new Error(msg);
  }

  const json: ApiResponse<MatchCandidateDto[]> = await response.json();
  return json.data ?? [];
}

/**
 * Devuelve links activos NAVIERA↔REPO_DOC por equipment para un booking.
 * Esta es la fuente de verdad granular; no se debe inferir desde LinkedBookingId.
 * @throws Error con mensaje del backend si la petición falla
 */
export async function getActiveEquipmentLinks(
  bookingId: number
): Promise<BookingEquipmentReconciliationLinkDto[]> {
  const url = appConfig.getApiUrl(`bookings/equipment-reconciliation/by-booking/${bookingId}/active`);
  const response = await fetch(url, {
    method: "GET",
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const msg = await parseErrorMessage(response);
    throw new Error(msg);
  }

  const json: ApiResponse<BookingEquipmentReconciliationLinkDto[]> = await response.json();
  return json.data ?? [];
}

/**
 * Devuelve la vista read-only de salud de conciliacion desde el lado NAVIERA (un registro por booking NAVIERA).
 * Normaliza un `data` ausente y las colecciones omitidas por el backend.
 * @throws Error con mensaje del backend si la peticion falla
 */

const NAVIERA_HEALTH_CATEGORIES: NavieraHealthCategory[] = ["Resolved", "Partial", "Unlinked"];

/**
 * El Core serializa las claves de los diccionarios en camelCase (`resolved`, `partial`...),
 * mientras que el tipo del front usa las categorías en PascalCase. Normaliza las claves
 * sin distinguir mayúsculas para que los contadores no queden a 0.
 */
function normaliseNavieraCategoryCounts(
  raw: Record<string, number> | null | undefined
): Partial<Record<NavieraHealthCategory, number>> {
  const counts: Partial<Record<NavieraHealthCategory, number>> = {};
  if (!raw) return counts;
  for (const [key, value] of Object.entries(raw)) {
    const category = NAVIERA_HEALTH_CATEGORIES.find((item) => item.toLowerCase() === key.toLowerCase());
    if (category) counts[category] = (counts[category] ?? 0) + (Number(value) || 0);
  }
  return counts;
}

export async function getNavieraReconciliationHealth(
  request: GetNavieraReconciliationHealthRequest
): Promise<NavieraReconciliationHealthDto> {
  const url = appConfig.getApiUrl("bookings/reconciliation/health/naviera/query");
  const response = await fetch(url, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const msg = await parseErrorMessage(response);
    throw new Error(msg);
  }

  const json: ApiResponse<NavieraReconciliationHealthDto> = await response.json();
  const data = json.data;
  return {
    summary: {
      totalNaviera: data?.summary?.totalNaviera ?? 0,
      countsByCategory: normaliseNavieraCategoryCounts(data?.summary?.countsByCategory),
      groupsByNaviera: (data?.summary?.groupsByNaviera ?? []).map((group) => ({
        ...group,
        countsByCategory: normaliseNavieraCategoryCounts(group.countsByCategory),
      })),
    },
    rows: (data?.rows ?? []).map((row) => ({
      ...row,
      equipment: row.equipment ?? { total: 0, linked: 0, pending: 0, withContract: 0 },
      counterparts: (row.counterparts ?? []).map((counterpart) => ({
        ...counterpart,
        levels: counterpart.levels ?? [],
        links: counterpart.links ?? [],
      })),
    })),
    lastRow: data?.lastRow ?? 0,
  };
}

/**
 * Devuelve la radiografia read-only de salud de conciliacion REPO_DOC.
 * No ejecuta matching ni persiste links; solo consume el endpoint Core de diagnostico.
 * @throws Error con mensaje del backend si la peticion falla
 */
const RESOLUTION_FILTER_KEYS = ["resolutionWay", "resolutionLevel", "resolutionOrigin"] as const;

/** Empty resolution lists are dropped so the request stays identical to the one an older core expects. */
function serializeHealthRequest(request: GetRepoDocReconciliationHealthRequest): string {
  const body: GetRepoDocReconciliationHealthRequest = { ...request };
  for (const key of RESOLUTION_FILTER_KEYS) {
    if (!body[key]?.length) delete body[key];
  }
  return JSON.stringify(body);
}

export async function getRepoDocReconciliationHealth(
  request: GetRepoDocReconciliationHealthRequest
): Promise<RepoDocReconciliationHealthDto> {
  const url = appConfig.getApiUrl("bookings/reconciliation/health/query");
  const response = await fetch(url, {
    method: "POST",
    headers: getAuthHeaders(),
    body: serializeHealthRequest(request),
  });

  if (!response.ok) {
    const msg = await parseErrorMessage(response);
    throw new Error(msg);
  }

  const json: ApiResponse<RepoDocReconciliationHealthDto> = await response.json();
  const data = json.data ?? {
    summary: {
      totalRepoDocs: 0,
      countsByCategory: {},
      groupsByNaviera: [],
      sharedShipmentGroups: [],
    },
    rows: [],
    lastRow: 0,
  };

  return {
    ...data,
    summary: {
      ...data.summary,
      countsByCategory: data.summary.countsByCategory ?? {},
      groupsByNaviera: data.summary.groupsByNaviera ?? [],
      sharedShipmentGroups: (data.summary.sharedShipmentGroups ?? []).map((group) => {
        const pendingEquipmentIds = group.repoDocBookingEquipmentIds ?? [];
        const linkedEquipmentIds = group.linkedRepoDocBookingEquipmentIds ?? [];
        const allEquipmentIds = group.allRepoDocBookingEquipmentIds
          ?? Array.from(new Set([...pendingEquipmentIds, ...linkedEquipmentIds]));

        return {
          ...group,
          repoDocBookingIds: group.repoDocBookingIds ?? [],
          repoDocBookingEquipmentIds: pendingEquipmentIds,
          allRepoDocBookingEquipmentIds: allEquipmentIds,
          linkedRepoDocBookingEquipmentIds: linkedEquipmentIds,
          canBulkConfirm: group.canBulkConfirm && pendingEquipmentIds.length > 0,
          canComplete: group.canComplete === true && pendingEquipmentIds.length > 0,
        };
      }),
    },
    rows: data.rows ?? [],
    lastRow: data.lastRow ?? 0,
  };
}

/**
 * Confirma una conciliación explícita booking-level. Se usa únicamente cuando
 * REPO_DOC no tiene equipment utilizable; Core vuelve a validar la evidencia.
 */
export async function confirmBookingLevelReconciliation(
  request: ConfirmBookingLevelReconciliationRequest,
): Promise<string> {
  const url = appConfig.getApiUrl("bookings/reconciliation/booking-level/confirm");
  const response = await fetch(url, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    throw new Error(await parseErrorMessage(response));
  }

  const json: ApiResponse<string> = await response.json();
  return json.data ?? "Conciliación booking-level confirmada";
}

/** Inactiva la conciliación booking-level activa del REPO_DOC indicado. */
export async function unlinkBookingLevelReconciliation(
  request: UnlinkBookingLevelReconciliationRequest,
): Promise<string> {
  const url = appConfig.getApiUrl("bookings/reconciliation/booking-level/unlink");
  const response = await fetch(url, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    throw new Error(await parseErrorMessage(response));
  }

  const json: ApiResponse<string> = await response.json();
  return json.data ?? "Conciliación booking-level revertida";
}


/**
 * Devuelve la salud read-only de asignacion de equipos NAVIERA a lineas de contrato.
 * No asigna contratos; solo diagnostica bloqueos, candidatos y senales explicables.
 * @throws Error con mensaje del backend si la peticion falla
 */
export async function getContractAssignmentHealth(
  request: GetContractAssignmentHealthRequest
): Promise<ContractAssignmentHealthDto> {
  const url = appConfig.getApiUrl("bookings/contract-assignment-health/query");
  const response = await fetch(url, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const msg = await parseErrorMessage(response);
    throw new Error(msg);
  }

  const json: ApiResponse<ContractAssignmentHealthDto> = await response.json();
  return json.data ?? {
    summary: {
      totalRows: 0,
      pendingRows: 0,
      assignedRows: 0,
      blockedRows: 0,
      confirmableRows: 0,
      countsByReasonCode: {},
      countsByOperationType: {},
    },
    rows: [],
    lastRow: 0,
  };
}


/**
 * Devuelve candidatos paginados y filtrables para un equipo puntual de la salud de asignacion.
 * El endpoint es GET y no escribe nada; la confirmacion sigue pasando por Core.
 */
export async function getContractAssignmentHealthCandidates(
  request: GetContractAssignmentHealthCandidatesRequest
): Promise<ContractAssignmentHealthCandidatesDto> {
  const baseUrl = appConfig.getApiUrl("bookings/contract-assignment-health/candidates");
  const url = appendQuery(baseUrl, request as unknown as Record<string, unknown>);
  const response = await fetch(url, {
    method: "GET",
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const msg = await parseErrorMessage(response);
    throw new Error(msg);
  }

  const json: ApiResponse<ContractAssignmentHealthCandidatesDto> = await response.json();
  const data = json.data;
  return {
    bookingContext: data?.bookingContext ?? null,
    candidates: Array.isArray(data?.candidates) ? data.candidates : [],
    totalRows: data?.totalRows ?? 0,
    totalRowsLabel: data?.totalRowsLabel ?? String(data?.totalRows ?? 0),
    shownRows: data?.shownRows ?? (Array.isArray(data?.candidates) ? data.candidates.length : 0),
    startRow: data?.startRow ?? request.startRow ?? 0,
    endRow: data?.endRow ?? request.endRow ?? 0,
    lastRow: data?.lastRow ?? request.startRow ?? 0,
    limitApplied: data?.limitApplied ?? null,
    hasMoreRows: data?.hasMoreRows ?? false,
  };
}

/**
 * Confirma una asignacion manual segura desde la vista de salud.
 * El backend revalida que el equipo siga conciliado, pendiente y con candidato vigente.
 * @throws Error con mensaje del backend si la peticion falla
 */
export async function confirmContractAssignment(
  request: ConfirmContractAssignmentRequest
): Promise<ConfirmContractAssignmentResponse> {
  const url = appConfig.getApiUrl("bookings/contract-assignment-health/confirm");
  const response = await fetch(url, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const msg = await parseErrorMessage(response);
    throw new Error(msg);
  }

  const json: ApiResponse<ConfirmContractAssignmentResponse> = await response.json();
  return json.data;
}

/**
 * Confirma manualmente un grupo de embarque compartido.
 * Crea links many-to-one desde un equipment NAVIERA hacia varios equipment REPO_DOC.
 * @throws Error con mensaje del backend si la peticion falla
 */
export async function confirmSharedShipment(
  request: ConfirmSharedShipmentRequest
): Promise<ConfirmSharedShipmentResponse> {
  const url = appConfig.getApiUrl("bookings/reconciliation/shared-shipment/confirm");
  const response = await fetch(url, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const msg = await parseErrorMessage(response);
    throw new Error(response.status === 409
      ? `${msg}. Actualizá los candidatos antes de reintentar.`
      : msg);
  }

  try {
    const json: ApiResponse<unknown> & { message?: string | null } = await response.json();
    return { message: json.message?.trim() || CONFIRM_SHARED_SHIPMENT_SUCCESS_MESSAGE };
  } catch {
    return { message: CONFIRM_SHARED_SHIPMENT_SUCCESS_MESSAGE };
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// POST bookings/get/advanced — búsqueda libre de bookings NAVIERA
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Busca bookings NAVIERA por referencia o MBL (búsqueda libre para vinculación manual).
 * Usa el endpoint estándar de bookings con filterGroups: source=NAVIERA + texto libre.
 * @throws Error con mensaje del backend si la petición falla
 */
export async function searchNavieraBookings(
  searchTerm: string
): Promise<NavieraBookingSearchResult[]> {
  const url = appConfig.getApiUrl("bookings/get/advanced");
  const trimmed = searchTerm.trim();

  // Construir filterGroups: source=NAVIERA AND (reference CONTAINS term OR mbl CONTAINS term)
  const filterGroups = [
    [
      { property: "source", value: "NAVIERA", operator: "Equals" },
      { property: "bookingReference", value: trimmed, operator: "Contains" },
    ],
    [
      { property: "source", value: "NAVIERA", operator: "Equals" },
      { property: "mbl", value: trimmed, operator: "Contains" },
    ],
  ];

  const response = await fetch(url, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify({
      startRow: 0,
      endRow: 20,
      sortProperty: "bookingDate",
      sortDirection: "desc",
      filterGroups,
      includeRelated: true,
      includeArchived: false,
    }),
  });

  if (!response.ok) {
    const msg = await parseErrorMessage(response);
    throw new Error(msg);
  }

  const json = await response.json();
  // El endpoint /bookings/get/advanced devuelve { data: { rows: [...], lastRow, totalBookings, ... } }.
  // Cuando no hay resultados puede devolver { data: { lastRow: 0, ... } }, que NO es iterable.
  const data: any[] = Array.isArray(json.data?.rows)
    ? json.data.rows
    : Array.isArray(json.data)
      ? json.data
      : Array.isArray(json.items)
        ? json.items
        : [];
  return data.map((b) => ({
    bookingId: b.id,
    bookingReference: b.booking_reference ?? b.bookingReference ?? null,
    mbl: b.mbl ?? null,
    containerId: b.equipments?.[0]?.container_id ?? null,
    etd: b.etd ?? null,
  }));
}

// ═══════════════════════════════════════════════════════════════════════════
// POST api/bookings/reconciliation/link
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Vincula manualmente bookings NAVIERA + REPO_DOC. Cuando el caller envía
 * equipmentLinks, el backend persiste sólo esos pares equipment/container.
 *
 * Errores posibles del backend:
 *   400 — mismo Source en ambos, o alguno ya vinculado
 *   404 — booking no encontrado
 *
 * @throws Error con mensaje del backend si la petición falla
 */
export async function linkBookingEquipment(
  request: LinkBookingEquipmentRequest
): Promise<void> {
  const url = appConfig.getApiUrl("bookings/reconciliation/link");
  const response = await fetch(url, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const msg = await parseErrorMessage(response);
    throw new Error(msg);
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// POST api/bookings/reconciliation/unlink
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Desvincula un booking de su par.
 *
 * Errores posibles del backend:
 *   400 — booking no vinculado, o tiene factura interna emitida
 *   404 — booking no encontrado
 *
 * @throws Error con mensaje del backend si la petición falla
 */
export async function unlinkBooking(
  request: UnlinkBookingRequest
): Promise<void> {
  const url = appConfig.getApiUrl("bookings/reconciliation/unlink");
  const response = await fetch(url, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const msg = await parseErrorMessage(response);
    throw new Error(msg);
  }
}
